import React, { useCallback, useEffect, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TerminalOutlinedIcon from '@mui/icons-material/TerminalOutlined';
import { GetAdminPhpLogTailAPI } from '../../../../api/AdminAPI';

const LINES_TO_FETCH = 200;

/**
 * PhpLogTail - Bloco colapsado com as últimas linhas de php.log (texto nativo do PHP,
 * não JSON) do dia selecionado. Carrega o conteúdo só quando expandido (Admin Geral > Logs).
 * @param {Object} props
 * @param {string} props.date - YYYY-MM-DD
 */
function PhpLogTail({ date }) {
  const [expanded, setExpanded] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lines, setLines] = useState([]);
  const [totalLines, setTotalLines] = useState(0);
  const [truncated, setTruncated] = useState(false);

  const fetchTail = useCallback(() => {
    setLoading(true);
    setError(null);
    GetAdminPhpLogTailAPI(date, LINES_TO_FETCH, (response) => {
      setLoading(false);
      if (!response || response.success === false) {
        setError(response?.error || 'Erro ao carregar o php.log. Tente novamente.');
        return;
      }
      setLines(response.lines || []);
      setTotalLines(response.total_lines || 0);
      setTruncated(Boolean(response.truncated));
      setLoaded(true);
    });
  }, [date]);

  // Se a data mudar com o bloco já expandido, recarrega para o novo dia.
  useEffect(() => {
    setLoaded(false);
    if (expanded) fetchTail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const handleToggle = (_, isExpanded) => {
    setExpanded(isExpanded);
    if (isExpanded && !loaded) fetchTail();
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={handleToggle}
      sx={{
        borderRadius: '12px !important',
        border: '1px solid #E8ECF0',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        '&:before': { display: 'none' },
        mt: 3,
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TerminalOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            php.log{loaded ? ` (últimas ${lines.length} de ${totalLines} linhas)` : ''}
          </Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : loaded ? (
          lines.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Nenhuma linha registrada neste dia.</Typography>
          ) : (
            <>
              {truncated && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  Mostrando apenas as últimas {lines.length} linhas. O arquivo tem {totalLines} linha(s) no total.
                </Alert>
              )}
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1.5,
                  bgcolor: '#0B0F14',
                  color: '#D6E2EF',
                  borderRadius: '8px',
                  fontSize: '0.75rem',
                  fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
                  overflowX: 'auto',
                  maxHeight: 360,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {lines.join('\n')}
              </Box>
            </>
          )
        ) : null}
      </AccordionDetails>
    </Accordion>
  );
}

export default PhpLogTail;
